import { StateCreator } from 'zustand';

import type {
  ComposeSnapshotIndexEntry,
  ComposeSnapshotIndexState,
  ComposeSnapshotIndexStore,
} from './types';
import { ComposeSnapshotChangeType } from '../../enums/composeSnapshotChangeType';
import { ComposeSnapshotStatus } from '../../enums/composeSnapshotStatus';
import type { ComposeSnapshot } from '../../models/composeSnapshot';

export const INITIAL_COMPOSE_SNAPSHOT_INDEX_STATE: ComposeSnapshotIndexState = {
  userId: undefined,
  entriesBySessionId: {},
  liveSessionIds: {},
};

export const toIndexEntry = (
  snapshot: ComposeSnapshot,
): ComposeSnapshotIndexEntry => ({
  sessionId: snapshot.sessionId,
  draftId: snapshot.draftId,
  sourceMessageId: snapshot.sourceMessageId,
  handler: snapshot.handler,
  inboxEntryId: snapshot.inboxEntryId,
  updatedAt: snapshot.updatedAt,
});

const withoutKey = <T>(
  record: Record<string, T>,
  key: string,
): Record<string, T> => {
  const { [key]: _removed, ...rest } = record;
  return rest;
};

export const createComposeSnapshotIndexSlice: StateCreator<
  ComposeSnapshotIndexStore
> = (set) => ({
  ...INITIAL_COMPOSE_SNAPSHOT_INDEX_STATE,

  hydrate: (userId, snapshots) =>
    set({
      userId,
      entriesBySessionId: Object.fromEntries(
        snapshots
          .filter((s) => s.status === ComposeSnapshotStatus.DIRTY)
          .map((s) => [s.sessionId, toIndexEntry(s)]),
      ),
    }),

  applyChange: (change) =>
    set((state) => {
      if (change.type === ComposeSnapshotChangeType.PUT) {
        const { snapshot } = change;
        if (
          snapshot.userId !== state.userId ||
          snapshot.status !== ComposeSnapshotStatus.DIRTY
        ) {
          return state;
        }
        return {
          entriesBySessionId: {
            ...state.entriesBySessionId,
            [snapshot.sessionId]: toIndexEntry(snapshot),
          },
        };
      }
      if (
        change.userId !== state.userId ||
        !(change.sessionId in state.entriesBySessionId)
      ) {
        return state;
      }
      return {
        entriesBySessionId: withoutKey(
          state.entriesBySessionId,
          change.sessionId,
        ),
      };
    }),

  registerLiveSession: (sessionId) =>
    set((state) => ({
      liveSessionIds: { ...state.liveSessionIds, [sessionId]: true },
    })),

  unregisterLiveSession: (sessionId) =>
    set((state) => ({
      liveSessionIds: withoutKey(state.liveSessionIds, sessionId),
    })),

  resetState: () => set(INITIAL_COMPOSE_SNAPSHOT_INDEX_STATE),
});
